"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CookieSettingsModal } from "@/components/cookie-settings-modal";

export interface CookiePreferences {
  necessary: boolean; // Always true
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  functional: false,
  marketing: false,
};

export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    localStorage.setItem("cookie-consent", "accepted");
    localStorage.setItem("cookie-preferences", JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    localStorage.setItem("cookie-consent", "rejected");
    localStorage.setItem("cookie-preferences", JSON.stringify(DEFAULT_PREFERENCES));
    setIsVisible(false);
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
  };

  const handleSavePreferences = (preferences: CookiePreferences) => {
    localStorage.setItem("cookie-consent", "custom");
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));
    setShowSettings(false);
    setIsVisible(false);
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
            style={{ WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="relative bg-background border border-border/40 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden">
                {/* Subtle gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent opacity-50 pointer-events-none" />

                <div className="relative p-4 sm:p-6">
                  {/* Close button */}
                  <button
                    onClick={() => setIsVisible(false)}
                    className="absolute top-3 right-3 sm:top-4 sm:right-4 text-foreground-secondary hover:text-foreground transition-colors p-1 rounded-lg hover:bg-foreground/5"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>

                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8 pr-8 lg:pr-0">
                    {/* Left side: Text content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[17px] sm:text-xl font-bold text-foreground mb-2">
                        We value your privacy
                      </h3>
                      <p className="text-[14px] sm:text-sm text-foreground-secondary leading-relaxed">
                        We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or withdraw your consent at any time.
                      </p>
                    </div>

                    {/* Right side: Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:shrink-0 lg:min-w-fit">
                      <Button
                        onClick={handleAcceptAll}
                        className="h-12 sm:h-11 text-[15px] bg-accent hover:bg-accent/90 text-background font-semibold whitespace-nowrap shadow-lg shadow-accent/20 hover:shadow-accent/30 transition-all px-5"
                      >
                        Accept All
                      </Button>
                      <Button
                        onClick={handleRejectNonEssential}
                        variant="outline"
                        className="h-12 sm:h-11 text-[15px] sm:text-[14px] border-border hover:bg-foreground/5 hover:border-foreground/30 font-medium whitespace-nowrap px-4"
                      >
                        Reject Non-Essential
                      </Button>
                      <Button
                        onClick={handleOpenSettings}
                        variant="ghost"
                        className="h-12 sm:h-11 text-[15px] sm:text-[14px] text-foreground-secondary hover:text-foreground hover:bg-foreground/5 font-medium whitespace-nowrap px-3 gap-2"
                      >
                        <Settings className="w-4 h-4 shrink-0" />
                        <span>Cookie Settings</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <CookieSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
}
